import CheckIcon from '@mui/icons-material/Check'
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material'

import {
  TierConfig,
  formatAnnualAsMonthly,
  formatPrice
} from '../../config/subscriptionTiers'

interface PricingCardProps {
  tier: TierConfig
  isCurrentTier?: boolean
  isRecommended?: boolean
  isSignedIn: boolean
  billingInterval: 'monthly' | 'annual'
  onBillingIntervalChange: (interval: 'monthly' | 'annual') => void
  onSelect: (tierId: string, billingInterval: 'monthly' | 'annual') => void
}

/**
 * PricingCard Component
 *
 * Displays a single subscription tier with its features, pricing, and CTA button.
 *
 * Features:
 * - Shows tier name, price, and user capacity
 * - Lists all tier features with checkmarks
 * - Displays "Current Plan" badge if this is the user's active tier
 * - Displays "RECOMMENDED" badge for highlighted tiers
 * - Supports billing interval toggle (monthly/annual)
 * - Shows 10% discount badge for annual billing
 * - Displays monthly equivalent for annual pricing
 * - Renders appropriate CTA button based on auth state and tier type
 * - Hover effects and visual styling
 *
 * @param tier - The tier configuration to display
 * @param isCurrentTier - Whether this tier is the user's current active tier
 * @param isRecommended - Whether to highlight this tier as recommended
 * @param isSignedIn - Whether the user is currently signed in
 * @param billingInterval - The currently selected billing interval
 * @param onBillingIntervalChange - Callback when billing interval changes
 * @param onSelect - Callback when the CTA button is clicked
 */
const PricingCard = ({
  tier,
  isCurrentTier = false,
  isRecommended = false,
  isSignedIn,
  billingInterval,
  onBillingIntervalChange,
  onSelect
}: PricingCardProps) => {
  const isFree = tier.id === 'free'
  const isAnnual = billingInterval === 'annual'
  const currentPrice = isAnnual ? tier.priceAnnual : tier.priceMonthly

  // Determine button text based on state
  const getButtonText = () => {
    if (isCurrentTier) {
      return 'Current Plan'
    }
    if (isFree) {
      return 'Get Started'
    }
    if (isSignedIn) {
      return 'Select Plan'
    }
    return 'Sign Up'
  }

  return (
    <Card
      sx={{
        position: 'relative',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        border: isRecommended
          ? '3px solid #667eea'
          : '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: isRecommended
          ? '0 12px 24px rgba(102, 126, 234, 0.3)'
          : '0 4px 12px rgba(0, 0, 0, 0.1)',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: isRecommended
            ? '0 16px 32px rgba(102, 126, 234, 0.4)'
            : '0 12px 24px rgba(0, 0, 0, 0.15)'
        }
      }}
    >
      {/* Badges - Ribbon Style */}
      {isRecommended && !isCurrentTier && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: '#667eea',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '0.75rem',
            padding: '6px 16px',
            borderBottomLeftRadius: '8px',
            boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
            zIndex: 1
          }}
        >
          RECOMMENDED
        </Box>
      )}
      {isCurrentTier && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            backgroundColor: '#10b981',
            color: '#ffffff',
            fontWeight: 700,
            fontSize: '0.75rem',
            padding: '6px 16px',
            borderBottomLeftRadius: '8px',
            boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)',
            zIndex: 1
          }}
        >
          CURRENT PLAN
        </Box>
      )}

      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          p: 3
        }}
      >
        {/* Tier Name */}
        <Typography
          variant='h5'
          sx={{
            fontWeight: 700,
            color: '#1e293b',
            mb: 1
          }}
        >
          {tier.name}
        </Typography>

        {/* Billing Interval Toggle - Only for paid tiers */}
        {!isFree && (
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
            <ToggleButtonGroup
              value={billingInterval}
              exclusive
              onChange={(_, value) => {
                if (value !== null) {
                  onBillingIntervalChange(value)
                }
              }}
              size='small'
              sx={{
                '& .MuiToggleButton-root': {
                  px: 2,
                  py: 0.5,
                  fontSize: '0.875rem',
                  textTransform: 'none',
                  border: '1px solid rgba(102, 126, 234, 0.3)',
                  '&.Mui-selected': {
                    backgroundColor: '#667eea',
                    color: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#5568d3'
                    }
                  }
                }
              }}
            >
              <ToggleButton value='monthly'>Monthly</ToggleButton>
              <ToggleButton value='annual'>Annual</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        )}

        {/* Price */}
        <Box sx={{ mb: 3 }}>
          {isFree ? (
            <Typography
              variant='h3'
              sx={{
                fontWeight: 800,
                color: '#667eea'
              }}
            >
              Free
            </Typography>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
                <Typography
                  variant='h3'
                  sx={{
                    fontWeight: 800,
                    color: '#667eea'
                  }}
                >
                  {formatPrice(currentPrice)}
                </Typography>
                <Typography
                  variant='body1'
                  sx={{
                    ml: 1,
                    color: '#64748b'
                  }}
                >
                  {isAnnual ? '/year' : '/month'}
                </Typography>
              </Box>
              {/* Show monthly equivalent for annual billing */}
              {isAnnual && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mt: 1
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      color: '#64748b'
                    }}
                  >
                    {formatAnnualAsMonthly(tier.priceAnnual)} billed annually
                  </Typography>
                  <Chip
                    label='Save 10%'
                    size='small'
                    sx={{
                      backgroundColor: '#10b981',
                      color: '#ffffff',
                      fontWeight: 600,
                      fontSize: '0.75rem',
                      height: '20px'
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>

        {/* User Capacity */}
        <Typography
          variant='body1'
          sx={{
            fontWeight: 600,
            color: '#1e293b',
            mb: 3
          }}
        >
          Up to {tier.userCapacity} users
        </Typography>

        {/* Features */}
        <Box sx={{ flex: 1, mb: 3 }}>
          {tier.features.map((feature, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                mb: 1.5
              }}
            >
              <CheckIcon
                sx={{
                  color: '#667eea',
                  fontSize: '1.25rem',
                  mr: 1,
                  mt: 0.25
                }}
              />
              <Typography
                variant='body2'
                sx={{
                  color: '#64748b',
                  lineHeight: 1.6
                }}
              >
                {feature}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* CTA Button */}
        <Button
          variant={isRecommended && !isCurrentTier ? 'contained' : 'outlined'}
          fullWidth
          onClick={() => onSelect(tier.id, billingInterval)}
          disabled={isCurrentTier}
          sx={{
            py: 1.5,
            fontWeight: 600,
            fontSize: '1rem',
            borderRadius: 2,
            ...(isRecommended && !isCurrentTier
              ? {
                  backgroundColor: '#667eea',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#5568d3'
                  }
                }
              : {
                  borderColor: '#667eea',
                  color: '#667eea',
                  '&:hover': {
                    borderColor: '#5568d3',
                    backgroundColor: 'rgba(102, 126, 234, 0.04)'
                  }
                }),
            ...(isCurrentTier && {
              borderColor: '#10b981',
              color: '#10b981',
              cursor: 'default',
              '&:hover': {
                borderColor: '#10b981',
                backgroundColor: 'transparent'
              }
            })
          }}
        >
          {getButtonText()}
        </Button>
      </CardContent>
    </Card>
  )
}

export default PricingCard
